import {
  InfiniteData,
  UseInfiniteQueryResult,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { API } from 'api';
import { AxiosResponse } from 'axios';
import { OrganizationMember } from 'features/users/users.types';

type UseOrganizationMembersProps = {
  enabled?: boolean;
};

type UseOrganizationMembers = Omit<
  UseInfiniteQueryResult<
    InfiniteData<AxiosResponse<MembersPaginatedData>, unknown>,
    Error
  >,
  'data'
> & {
  members: OrganizationMember[];
};

type MembersPaginatedData = {
  count: number;
  results: OrganizationMember[];
  next: string | null;
  previous: string | null;
};

const PAGE_SIZE = 50;

const fetchOrganizationMembers = async ({
  limit,
  offset,
}: {
  limit: number;
  offset: number;
}): Promise<AxiosResponse<MembersPaginatedData>> => {
  return API.get<MembersPaginatedData>('organization/members/', {
    params: { limit, offset },
  });
};

export const useOrganizationMembers = (
  props: UseOrganizationMembersProps = {},
): UseOrganizationMembers => {
  const { enabled = true } = props;

  const { data, ...results } = useInfiniteQuery({
    queryKey: ['organizationMembers'],
    queryFn: ({ pageParam }) =>
      fetchOrganizationMembers({ limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      return lastPage.data.next ? lastPageParam + PAGE_SIZE : null;
    },
    enabled,
  });

  const members = (
    data?.pages.flatMap((page) => page.data.results) ?? []
  ).filter(Boolean) as OrganizationMember[];

  return { ...results, members };
};
